// A12 kimliğe bürünme afişi: SEROTA personeli bir panel kullanıcısının kimliğine büründüğünde ekranın üstünde
// kapatılamayan şerit — kim, neden, ne zamana kadar + "Oturumu sonlandır". Afiş akış içinde ve yapışkandır; yüksekliği
// `--sr-imp-h` değişkenine yazılır ve kabuğun 100vh hesapları (menü, üst bar, canlı harita ızgarası) bu kadar kısaltılır,
// böylece panel görünüm alanına sığmaya devam eder (paylaşılan ui-web stillerine dokunmadan, yalnız bu dosyada).
import { useEffect, useRef, useState } from 'react';
import { formatTime } from '@serota/i18n';
import { Button, Icon, useToast } from '@serota/ui-web';
import { api, ApiError, errorText, setCompanyId, tokenStore } from '../lib/api';
import { queryClient } from '../lib/query';
import { useSession } from '../lib/session';

const CSS = `
.sr-imp-banner { position: sticky; top: 0; z-index: 40; display: flex; align-items: center; gap: var(--space-3);
  padding: var(--space-2) var(--space-4); background: var(--warning-tint); color: var(--ink);
  border-bottom: 1px solid var(--warning); }
.sr-imp-banner__text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
body.sr-imp .sr-shell { min-height: calc(100vh - var(--sr-imp-h, 0px)); }
body.sr-imp .sr-side { top: var(--sr-imp-h, 0px); height: calc(100vh - var(--sr-imp-h, 0px)); }
body.sr-imp .sr-topbar { top: var(--sr-imp-h, 0px); }
body.sr-imp .sr-livegrid { height: calc(100vh - 65px - var(--sr-imp-h, 0px)); }
body.sr-imp [style*="calc(100vh - 65px)"] { height: calc(100vh - 65px - var(--sr-imp-h, 0px)) !important; }
`;

/** "16:30" → "16:30'a" (Türkçe yönelme eki saatin okunuşuna göre). */
function untilTime(iso: string): string {
  const t = formatTime(iso);
  const [h, m] = t.split(':').map(Number) as [number, number];
  const n = m === 0 ? h : m;
  const last = n % 10;
  const tens = Math.floor(n / 10) % 10;
  const byLast: Record<number, string> = { 1: 'e', 2: 'ye', 3: 'e', 4: 'e', 5: 'e', 6: 'ya', 7: 'ye', 8: 'e', 9: 'a' };
  const byTens: Record<number, string> = { 0: 'a', 1: 'a', 2: 'ye', 3: 'a', 4: 'a', 5: 'ye' };
  const suffix = last ? byLast[last]! : n === 0 ? 'a' : byTens[tens]!;
  return `${t}'${suffix}`;
}

export function ImpersonationBanner() {
  const { context } = useSession();
  const toast = useToast();
  const imp = context?.impersonation ?? null;
  const ref = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!imp || !el) return;
    const root = document.documentElement;
    document.body.classList.add('sr-imp');
    const sync = () => root.style.setProperty('--sr-imp-h', `${el.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.body.classList.remove('sr-imp');
      root.style.removeProperty('--sr-imp-h');
    };
  }, [imp]);

  if (!imp) return null;

  /** Sunucu oturumu kapalı: yerel oturum temizlenir + tam yeniden yükleme (çıkış ucu 401 döneceği için çağrılmaz). */
  const leave = async () => {
    await tokenStore.set(null);
    setCompanyId(null);
    queryClient.clear();
    window.location.replace('/giris');
  };

  const end = async () => {
    setBusy(true);
    try {
      await api.post('/v1/admin/impersonations/current/end');
      await leave();
    } catch (e) {
      // belirteç zaten geçersiz (süre doldu ya da Admin sonlandırdı): yalnız yerelden çık
      if (e instanceof ApiError && e.status === 401) return leave();
      // sunucu kapatamadıysa yerelde çıkış yapılmaz (oturum açık kalırdı): afiş durur, tekrar denenir
      setBusy(false);
      toast.show(`${errorText(e)} Oturum sonlandırılamadı; tekrar deneyin.`, { icon: 'uyari' });
    }
  };

  return (
    <>
      <style>{CSS}</style>
      <div ref={ref} className="sr-imp-banner" role="status" aria-live="polite">
        <Icon name="guvenlik" className="sr-warn-text" />
        <span className="sr-imp-banner__text body" title={imp.reason}>
          <strong>Kimliğe bürünme:</strong> {imp.adminName} · {imp.reason} · {untilTime(imp.expiresAt)} kadar
        </span>
        <Button size="sm" variant="secondary" icon="x" loading={busy} onClick={() => void end()}>
          Oturumu sonlandır
        </Button>
      </div>
    </>
  );
}
